import { useMemo } from 'react';
import { Button, Modal, ModalFooter, ModalHeader, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { faClouds } from '@fortawesome/pro-regular-svg-icons';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { StyledModalIcon, StyledModalTitle } from 'src/common/components/standard-action-modal/standard-action-modal.styles';
import { useJoinLabMutation, useLeaveLabMutation } from 'src/features/api/rooms/azure/azure.slice';
import { useLabExpiryCountdown } from './hooks/expires-in-countdown';
import { AzureCloudDetailsModalProps } from './azure-cloud-details-modal.types';
import { AzureTabs } from './components/azure-tabs';
export const AzureCloudDetailsModal = ({
  open,
  onOpenChange,
  companyRefreshing,
  taskId,
  roomId,
  lab
}: AzureCloudDetailsModalProps) => {
  const [joinLab, {
    isLoading: isJoining
  }] = useJoinLabMutation({
    fixedCacheKey: 'join-azure-lab'
  });
  const [leaveLab, {
    isLoading: isLeaving
  }] = useLeaveLabMutation({
    fixedCacheKey: 'leave-azure-lab'
  });
  const {
    formattedExpiry
  } = useLabExpiryCountdown(lab?.expires && new Date(lab?.expires));
  const handleJoinLab = () => {
    joinLab({
      taskId,
      roomId
    }).unwrap().catch((error: unknown) => NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Error occurred while trying to join lab, please try again.'));
  };
  const handleLeaveLab = () => {
    leaveLab({
      taskId,
      roomId,
      labId: lab?._id
    }).unwrap().catch(() => NotificationDispatch('error', 'Error occurred while trying to leave lab, please try again.'));
  };
  const updating = companyRefreshing || isJoining || isLeaving;
  const labState: {
    label: string;
    colour: 'success' | 'warning';
  } = useMemo(() => {
    if (lab) {
      if (lab.isExpired || formattedExpiry === null) {
        return {
          label: 'Expired',
          colour: 'warning'
        };
      }
      return {
        label: `Joined`,
        colour: 'success'
      };
    }
    return {
      label: 'Not Joined',
      colour: 'warning'
    };
  }, [lab, formattedExpiry]);
  const openUrl = lab?.openUrl || lab?.azureHost;
  return <Modal open={open} onOpenChange={onOpenChange} isSmall data-sentry-element="Modal" data-sentry-component="AzureCloudDetailsModal" data-sentry-source-file="azure-cloud-details-modal.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="azure-cloud-details-modal.tsx">
        <StyledModalIcon icon={faClouds} data-sentry-element="StyledModalIcon" data-sentry-source-file="azure-cloud-details-modal.tsx" />
        <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="azure-cloud-details-modal.tsx">{lab?.lab ?? 'Cloud Details'}</StyledModalTitle>
      </ModalHeader>
      <AzureTabs lab={lab} formattedExpiry={formattedExpiry} data-sentry-element="AzureTabs" data-sentry-source-file="azure-cloud-details-modal.tsx" />
      <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="azure-cloud-details-modal.tsx">
        <Button isDisabled={labState.label === 'Joined' || updating} disabled={labState.label === 'Joined' || updating} isLoading={isJoining} loadingMsg="Joining..." variant="solid" color="primary" type="button" onClick={handleJoinLab} data-sentry-element="Button" data-sentry-source-file="azure-cloud-details-modal.tsx">
          Join Lab
        </Button>
        <Button isDisabled={labState.label !== 'Joined' || updating} variant="solid" color="info" type="button" onClick={() => window.open(openUrl, '_blank')} data-sentry-element="Button" data-sentry-source-file="azure-cloud-details-modal.tsx">
          Open Lab
        </Button>

        <Button isDisabled={labState.label === 'Not Joined' || labState.label === 'Expired' || updating} isLoading={isLeaving} loadingMsg="Leaving..." variant="solid" color="hint" type="button" onClick={handleLeaveLab} data-sentry-element="Button" data-sentry-source-file="azure-cloud-details-modal.tsx">
          Leave Lab
        </Button>
      </ModalFooter>
    </Modal>;
};